/**
 * Loading Spinner Component
 *
 * Flexible loading spinner with multiple variants, sizes, and overlay options.
 * Used throughout the application for loading states.
 */

import { forwardRef } from "react";

// Spinner variants
const VARIANTS = {
  spinner: "animate-spin",
  pulse: "animate-pulse",
  bounce: "animate-bounce",
  ping: "animate-ping",
};

// Size configurations
const SIZES = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
  "2xl": "h-16 w-16",
};

// Color themes
const COLORS = {
  primary: "text-blue-600",
  secondary: "text-purple-600",
  success: "text-green-600",
  warning: "text-yellow-600",
  danger: "text-red-600",
  gray: "text-gray-600",
  white: "text-white",
};

/**
 * Basic Spinner Icon
 */
function SpinnerIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Dots Spinner
 */
function DotsSpinner({ className, color }) {
  return (
    <div className={`flex space-x-1 ${className}`}>
      <div
        className={`h-2 w-2 rounded-full ${color} animate-bounce`}
        style={{ animationDelay: "0ms" }}
      />
      <div
        className={`h-2 w-2 rounded-full ${color} animate-bounce`}
        style={{ animationDelay: "150ms" }}
      />
      <div
        className={`h-2 w-2 rounded-full ${color} animate-bounce`}
        style={{ animationDelay: "300ms" }}
      />
    </div>
  );
}

/**
 * Pulse Skeleton
 */
function PulseSkeleton({ className }) {
  return <div className={`bg-gray-300 rounded animate-pulse ${className}`} />;
}

/**
 * Main Loading Spinner Component
 */
const LoadingSpinner = forwardRef(function LoadingSpinner(
  {
    size = "md",
    variant = "spinner",
    color = "primary",
    overlay = false,
    fullscreen = false,
    text = null,
    textPosition = "bottom",
    className = "",
    ...props
  },
  ref
) {
  // Build classes
  const sizeClass = SIZES[size] || SIZES.md;
  const colorClass = COLORS[color] || COLORS.primary;
  const animationClass = VARIANTS[variant] || VARIANTS.spinner;

  // Render the appropriate spinner type
  const renderSpinner = () => {
    switch (variant) {
      case "dots":
        return <DotsSpinner className={sizeClass} color={colorClass} />;
      case "pulse":
        return <PulseSkeleton className={`${sizeClass} ${colorClass}`} />;
      case "ping":
        return (
          <div
            className={`${sizeClass} ${colorClass} ${animationClass} rounded-full bg-current opacity-75`}
          />
        );
      case "bounce":
        return (
          <div
            className={`${sizeClass} ${colorClass} ${animationClass} rounded bg-current`}
          />
        );
      default:
        return (
          <SpinnerIcon
            className={`${sizeClass} ${colorClass} ${animationClass}`}
          />
        );
    }
  };

  // Base content
  const content = (
    <div
      ref={ref}
      className={`flex items-center justify-center ${
        textPosition === "left"
          ? "flex-row space-x-2"
          : textPosition === "right"
            ? "flex-row-reverse space-x-reverse space-x-2"
            : textPosition === "top"
              ? "flex-col-reverse space-y-reverse space-y-2"
              : "flex-col space-y-2"
      } ${className}`}
      {...props}
    >
      {renderSpinner()}
      {text && (
        <div className={`text-sm font-medium ${colorClass}`}>{text}</div>
      )}
    </div>
  );

  // Handle fullscreen overlay
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90">
        {content}
      </div>
    );
  }

  // Handle overlay
  if (overlay) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75 rounded">
        {content}
      </div>
    );
  }

  return content;
});

/**
 * Inline Loading Component
 */
export function InlineLoading({
  text = "Loading...",
  size = "sm",
  color = "gray",
  className = "",
}) {
  return (
    <LoadingSpinner
      size={size}
      color={color}
      text={text}
      textPosition="right"
      className={`inline-flex ${className}`}
    />
  );
}

/**
 * Page Loading Component
 */
export function PageLoading({
  text = "Loading page...",
  size = "lg",
  className = "",
}) {
  return (
    <div className={`flex items-center justify-center min-h-64 ${className}`}>
      <LoadingSpinner
        size={size}
        text={text}
        variant="spinner"
        color="primary"
      />
    </div>
  );
}

/**
 * Section Loading Component
 */
export function SectionLoading({
  text = "Loading...",
  className = "",
  overlay = true,
}) {
  return (
    <LoadingSpinner
      size="md"
      text={text}
      overlay={overlay}
      className={className}
    />
  );
}

/**
 * Button Loading Component
 */
export function ButtonLoading({
  size = "xs",
  color = "white",
  className = "",
}) {
  return (
    <LoadingSpinner
      size={size}
      color={color}
      variant="spinner"
      className={`${className}`}
    />
  );
}

/**
 * Card Loading Skeleton
 */
export function CardLoadingSkeleton({ className = "" }) {
  return (
    <div
      className={`border border-gray-200 rounded-lg p-6 space-y-4 ${className}`}
    >
      <PulseSkeleton className="h-6 w-3/4" />
      <PulseSkeleton className="h-4 w-full" />
      <PulseSkeleton className="h-4 w-5/6" />
      <div className="flex space-x-4">
        <PulseSkeleton className="h-8 w-24" />
        <PulseSkeleton className="h-8 w-24" />
      </div>
    </div>
  );
}

/**
 * Table Loading Skeleton
 */
export function TableLoadingSkeleton({
  rows = 5,
  columns = 4,
  className = "",
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header skeleton */}
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <PulseSkeleton key={i} className="h-6 flex-1" />
        ))}
      </div>

      {/* Row skeletons */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex space-x-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <PulseSkeleton key={colIndex} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

// Export variants for convenience
LoadingSpinner.Inline = InlineLoading;
LoadingSpinner.Page = PageLoading;
LoadingSpinner.Section = SectionLoading;
LoadingSpinner.Button = ButtonLoading;
LoadingSpinner.CardSkeleton = CardLoadingSkeleton;
LoadingSpinner.TableSkeleton = TableLoadingSkeleton;

export default LoadingSpinner;
